import React, { useEffect, useMemo, useState } from 'react';
import { IconChevronLeft as ChevronLeft, IconCircle as Circle, IconSquare as Square, IconTriangle as Triangle, IconX as X } from '@tabler/icons-react';
import { boundsOf, SEAT_RADIUS, seatAbs, shapeCorners } from './geometry';
import { buildPattern, PATTERN_EXTRAS, patternCapacity, TEMPLATES, type PatternExtra, type PatternOptions, type PatternType } from './templates';
import { iconById } from './objectIcons';
import type { Plan, Point, TableShape } from './types';
import { cx } from './ui';

type Props = {
    onPick: (plan: Plan) => void;
    onBlank?: () => void;
    onClose: () => void;
};

const Preview: React.FC<{ plan: Plan }> = ({ plan }) => {
    const { seats, box } = useMemo(() => {
        const colors = new Map(plan.categories.map(c => [c.name, c.color]));
        const list: { p: Point; color: string }[] = [];
        plan.zones.forEach(z => z.rows.forEach(r => r.seats.forEach(s => list.push({ p: seatAbs(z, r, s), color: colors.get(s.category ?? '') ?? '#9CA3AF' }))));
        const pts = [...list.map(s => s.p), ...(plan.stages ?? []).flatMap(shapeCorners)];
        return { seats: list, box: boundsOf(pts, SEAT_RADIUS + 20) ?? { x: 0, y: 0, width: 100, height: 100 } };
    }, [plan]);

    return (
        <svg viewBox={`${box.x} ${box.y} ${box.width} ${box.height}`} className="h-full w-full" preserveAspectRatio="xMidYMid meet">
            {(plan.stages ?? []).map(s => {
                const { width: w, height: h } = s.size;
                const t = `translate(${s.position.x} ${s.position.y}) rotate(${s.rotation || 0} ${w / 2} ${h / 2})`;
                const fill = s.color || '#FDE68A';
                return (
                    <g key={s.id} transform={t}>
                        {s.type === 'circle' && <ellipse cx={w / 2} cy={h / 2} rx={w / 2} ry={h / 2} fill={fill} />}
                        {s.type === 'square' && <rect width={w} height={h} rx={s.curveRadius ?? 0} fill={fill} />}
                        {s.type === 'polygon' && s.points && <polygon points={s.points.map(p => `${p.x},${p.y}`).join(' ')} fill={fill} />}
                    </g>
                );
            })}
            {seats.map((s, i) => <circle key={i} cx={s.p.x} cy={s.p.y} r={SEAT_RADIUS} fill={s.color} />)}
        </svg>
    );
};

const dots = (cx0: number, cy0: number, r: number, n: number) =>
    Array.from({ length: n }, (_, i) => {
        const a = (i * 2 * Math.PI) / n;
        return <circle key={`${cx0}-${cy0}-${i}`} cx={cx0 + r * Math.cos(a)} cy={cy0 + r * Math.sin(a)} r={2.6} />;
    });

const tableCluster = (x: number, y: number) => (
    <g key={`t${x}-${y}`}>
        <circle cx={x} cy={y} r={5} />
        {dots(x, y, 10, 8)}
    </g>
);

const seatGrid = (x: number, y: number, cols: number, rows: number) =>
    Array.from({ length: rows * cols }, (_, i) => <circle key={`g${x}-${y}-${i}`} cx={x + (i % cols) * 7} cy={y + Math.floor(i / cols) * 7} r={2.6} />);

const LAYOUT_TYPES: { id: PatternType | 'blank'; label: string; art: React.ReactNode }[] = [
    { id: 'tables', label: 'Tables & Chairs', art: <>{[[25, 22], [55, 22], [85, 22], [40, 50], [70, 50]].map(([x, y]) => tableCluster(x, y))}</> },
    { id: 'sections', label: 'Sections & Rows', art: <><rect x={26} y={10} width={58} height={12} rx={1} fill="none" strokeWidth={1.2} />{seatGrid(19, 32, 6, 3)}{seatGrid(63, 32, 6, 3)}{seatGrid(22, 56, 11, 1)}</> },
    { id: 'mixed', label: 'Mixed Seating', art: <>{[[25, 20], [55, 20], [85, 20]].map(([x, y]) => tableCluster(x, y))}{seatGrid(20, 46, 11, 2)}</> },
    { id: 'blank', label: 'Blank Canvas', art: <><circle cx={55} cy={36} r={18} fill="none" strokeWidth={2} /><path d="M55 26v20M45 36h20" fill="none" strokeWidth={2} /></> },
];

const NumberRow: React.FC<{ label: string; value: number; min: number; max: number; onChange: (v: number) => void }> = ({ label, value, min, max, onChange }) => (
    <label className="flex items-center justify-between gap-4 py-1.5 text-sm text-gray-700 dark:text-nightTextSecondary">
        <span>{label}</span>
        <input
            type="number"
            inputMode="numeric"
            min={min}
            max={max}
            value={value}
            onChange={e => {
                const v = Math.round(Number(e.target.value));
                if (Number.isFinite(v)) onChange(Math.max(min, Math.min(max, v)));
            }}
            className="w-16 border-0 border-b border-gray-300 bg-transparent px-1 py-0.5 text-right text-sm tabular-nums focus:border-primary40 focus:outline-none focus:ring-0 dark:border-nightBorder dark:focus:border-nightAccent"
        />
    </label>
);

const PatternForm: React.FC<{ type: PatternType; onBack: () => void; onClose: () => void; onDone: (plan: Plan) => void }> = ({ type, onBack, onClose, onDone }) => {
    const [o, setO] = useState<PatternOptions>({
        type,
        sections: type === 'mixed' ? 2 : 4,
        rowsPerSection: 5,
        seatsPerRow: 10,
        tables: type === 'mixed' ? 6 : 10,
        seatsPerTable: 8,
        tableShape: 'round',
        extras: ['stage'],
    });
    const set = (patch: Partial<PatternOptions>) => setO(prev => ({ ...prev, ...patch }));
    const toggle = (e: PatternExtra) => set({ extras: o.extras.includes(e) ? o.extras.filter(x => x !== e) : [...o.extras, e] });
    const title = LAYOUT_TYPES.find(t => t.id === type)?.label;
    const shapes: [TableShape, string, typeof Circle][] = [['round', 'Round', Circle], ['rect', 'Square', Square], ['triangle', 'Triangle', Triangle]];

    return (
        <div className="flex w-full max-w-xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-nightSurface">
            <div className="relative flex h-14 items-center justify-center border-b border-gray-200 bg-gray-50 dark:border-nightBorder dark:bg-nightElevated">
                <button type="button" aria-label="Back" onClick={onBack} className="absolute left-3 flex h-8 w-8 items-center justify-center rounded text-gray-500 hover:bg-gray-200 dark:text-nightMuted dark:hover:bg-nightSurface">
                    <ChevronLeft className="h-5 w-5" />
                </button>
                <h2 className="text-base font-semibold text-gray-900 dark:text-nightTextSecondary">{title}</h2>
                <button type="button" aria-label="Close" onClick={onClose} className="absolute right-3 flex h-8 w-8 items-center justify-center rounded text-gray-500 hover:bg-gray-200 dark:text-nightMuted dark:hover:bg-nightSurface">
                    <X className="h-4 w-4" />
                </button>
            </div>
            <div className="overflow-y-auto px-6 py-5 sm:px-10">
                <div className="mx-auto max-w-sm">
                    {type !== 'tables' && (
                        <>
                            <NumberRow label="Number of sections" value={o.sections} min={1} max={60} onChange={sections => set({ sections })} />
                            <NumberRow label="Rows per section" value={o.rowsPerSection} min={1} max={100} onChange={rowsPerSection => set({ rowsPerSection })} />
                            <NumberRow label="Seats per row" value={o.seatsPerRow} min={1} max={100} onChange={seatsPerRow => set({ seatsPerRow })} />
                        </>
                    )}
                    {type !== 'sections' && (
                        <>
                            <NumberRow label="Number of tables" value={o.tables} min={1} max={200} onChange={tables => set({ tables })} />
                            <NumberRow label="Seats per table" value={o.seatsPerTable} min={1} max={30} onChange={seatsPerTable => set({ seatsPerTable })} />
                            <div className="flex items-center justify-between gap-4 py-1.5 text-sm text-gray-700 dark:text-nightTextSecondary">
                                <span>Table style</span>
                                <div className="flex gap-1">
                                    {shapes.map(([id, label, Icon]) => (
                                        <button
                                            key={id}
                                            type="button"
                                            title={label}
                                            aria-pressed={o.tableShape === id}
                                            onClick={() => set({ tableShape: id })}
                                            className={cx(
                                                'flex h-8 w-8 items-center justify-center rounded border',
                                                o.tableShape === id ? 'border-primary40 bg-primary40 text-white dark:border-nightAccent dark:bg-nightAccent dark:text-nightBg' : 'border-gray-300 text-gray-600 dark:border-nightBorder dark:text-nightTextSecondary',
                                            )}
                                        >
                                            <Icon className="h-4 w-4" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
                <p className="mt-5 text-center text-xs text-gray-500 dark:text-nightMuted">Also add</p>
                <div className="mt-2 flex flex-wrap justify-center gap-2">
                    {PATTERN_EXTRAS.map(e => {
                        const Icon = iconById(e.id === 'dance' ? 'dance' : e.id);
                        const on = o.extras.includes(e.id);
                        return (
                            <button
                                key={e.id}
                                type="button"
                                aria-pressed={on}
                                onClick={() => toggle(e.id)}
                                className={cx(
                                    'flex h-[72px] w-[72px] flex-col items-center justify-center gap-1.5 rounded text-xs transition-colors',
                                    on ? 'bg-primary40 text-white dark:bg-nightAccent dark:text-nightBg' : 'text-gray-600 hover:bg-gray-100 dark:text-nightTextSecondary dark:hover:bg-nightElevated',
                                )}
                            >
                                {Icon && <Icon className="h-6 w-6" strokeWidth={1.5} />}
                                {e.label}
                            </button>
                        );
                    })}
                </div>
                <p className="mt-5 text-center text-sm text-gray-700 dark:text-nightTextSecondary">Total: <span className="font-semibold tabular-nums">{patternCapacity(o)}</span></p>
            </div>
            <div className="flex justify-end border-t border-gray-200 px-6 py-4 dark:border-nightBorder">
                <button
                    type="button"
                    onClick={() => onDone(buildPattern(o))}
                    className="h-10 rounded bg-primary40 px-8 text-sm font-semibold text-white hover:bg-primary30 dark:bg-nightAccent dark:text-nightBg"
                >
                    Continue
                </button>
            </div>
        </div>
    );
};

const TemplatesModal: React.FC<Props> = ({ onPick, onBlank, onClose }) => {
    const [step, setStep] = useState<PatternType | null>(null);
    const items = useMemo(() => TEMPLATES.map(t => {
        const plan = t.build();
        const seats = plan.zones.reduce((n, z) => n + z.rows.reduce((m, r) => m + r.seats.length, 0), 0);
        return { ...t, plan, seats };
    }), []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    if (step) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-6" onMouseDown={e => e.target === e.currentTarget && onClose()}>
                <PatternForm type={step} onBack={() => setStep(null)} onClose={onClose} onDone={onPick} />
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-6" onMouseDown={e => e.target === e.currentTarget && onClose()}>
            <div className="flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-nightSurface">
                <div className="relative flex h-14 shrink-0 items-center justify-center border-b border-gray-200 bg-gray-50 dark:border-nightBorder dark:bg-nightElevated">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-nightTextSecondary">Choose a layout type</h2>
                    <button type="button" aria-label="Close" onClick={onClose} className="absolute right-3 h-8 w-8 flex items-center justify-center rounded text-gray-500 hover:bg-gray-200 dark:text-nightMuted dark:hover:bg-nightSurface">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="overflow-y-auto">
                <div className="mx-auto grid max-w-md grid-cols-2 gap-x-8 gap-y-5 px-4 py-6 sm:gap-x-16">
                    {LAYOUT_TYPES.filter(t => t.id !== 'blank' || onBlank).map(t => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => (t.id === 'blank' ? onBlank?.() : setStep(t.id))}
                            className="group flex flex-col items-center gap-2 text-sm text-primary40 dark:text-nightAccent"
                        >
                            <span className="flex aspect-[110/72] w-full items-center justify-center rounded border border-gray-300 bg-gray-50 transition-colors group-hover:border-primary40 dark:border-nightBorder dark:bg-nightSecondary dark:group-hover:border-nightAccent">
                                <svg viewBox="0 0 110 72" className="h-full w-full fill-none stroke-sky-500" strokeWidth={1}>{t.art}</svg>
                            </span>
                            {t.label}
                        </button>
                    ))}
                </div>
                <div className="border-t border-gray-200 px-4 pt-4 dark:border-nightBorder sm:px-5">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-nightTextSecondary">Or start from a venue template</h3>
                    <p className="text-xs text-gray-500 dark:text-nightMuted">Pick a common layout, then adjust sections, rows and tables to match your venue.</p>
                </div>
                <div className="grid grid-cols-2 content-start gap-3 p-3 sm:gap-4 sm:p-5 md:grid-cols-3 lg:grid-cols-4">
                    {items.map(t => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => onPick(t.build())}
                            className="group flex flex-col overflow-hidden rounded-lg border border-gray-200 text-left transition-all hover:border-primary40 hover:shadow-md dark:border-nightBorder dark:hover:border-nightAccent"
                        >
                            <div className="h-28 sm:h-36 shrink-0 bg-gray-50 p-2 sm:p-3 dark:bg-nightSecondary">
                                <Preview plan={t.plan} />
                            </div>
                            <div className="p-3">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-sm font-medium text-gray-900 dark:text-nightTextSecondary">{t.name}</span>
                                    <span className="text-[10px] tabular-nums text-gray-400">{t.seats} seats</span>
                                </div>
                                <div className="text-[11px] text-gray-500 dark:text-nightMuted">{t.description}</div>
                            </div>
                        </button>
                    ))}
                </div>
                </div>
            </div>
        </div>
    );
};

export default TemplatesModal;
